import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Modal, Form, Spinner, Alert, Row, Col, Toast, ToastContainer } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
    fetchAllStages,
    createStage,
    updateStage,
    deleteStage,
    clearError
} from '../../redux/slices/stagesSlice';
import {
    selectStagesSortedByOrder,
    selectStagesLoading,
    selectStagesError
} from '../../redux/selectors/stageSelectors';
import { emailTemplateService } from '../../services/api/emailTemplateService';
import { Stage, EmailTemplate, StageCreateDto, StageUpdateDto } from '../../types/types';
import LoadingSpinner from '../common/LoadingSpinner';

const StageManagement: React.FC = () => {
    const dispatch = useAppDispatch();

    // Redux state
    const stages = useAppSelector(selectStagesSortedByOrder);
    const loading = useAppSelector(selectStagesLoading);
    const error = useAppSelector(selectStagesError);

    // Local state
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
    const [currentStage, setCurrentStage] = useState<Stage | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const [showInactiveStages, setShowInactiveStages] = useState(false); // Hidden by default
    const [toast, setToast] = useState<{ show: boolean; message: string; variant: string }>({
        show: false,
        message: '',
        variant: 'success'
    });

    useEffect(() => {
        // Fetch stages on mount
        dispatch(fetchAllStages());

        // Fetch email templates
        const fetchEmailTemplates = async () => {
            setEmailTemplatesLoading(true);
            try {
                const templatesData = await emailTemplateService.getAllEmailTemplates();
                setEmailTemplates(templatesData.items);
            } catch (err) {
                console.error('Error fetching email templates:', err);
                showToast('Failed to load email templates', 'danger');
            } finally {
                setEmailTemplatesLoading(false);
            }
        };

        fetchEmailTemplates();
    }, [dispatch]);

    // Clear Redux error when component unmounts
    useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    // Filter stages based on showInactiveStages toggle
    const filteredStages = stages.filter(stage => {
        if (showInactiveStages) {
            return true; // Show all stages
        }
        return stage.isActive; // Show only active stages
    });

    const handleCreateStage = () => {
        const newStage: Stage = {
            id: '',
            name: '',
            description: '',
            displayOrder: Math.max(0, ...stages.map(s => s.displayOrder)) + 1,
            isActive: true,
            requiresEmail: false,
            emailTemplateId: null,
            autoAdvanceAfterDays: false,
            daysToAutoAdvance: null,
            advanceToStageId: null,
            canMoveToFromAnyStage: false,
            createdAt: new Date(),
            autoEmail: false,
            candidateCount: 0
        };
        setCurrentStage(newStage);
        setShowModal(true);
    };

    const handleEditStage = (stage: Stage) => {
        setCurrentStage({ ...stage });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentStage(null);
        dispatch(clearError());
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLElement>) => {
        if (!currentStage) return;

        const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        const { name, value, type } = target;
        let updatedValue: any = value;

        if (type === 'checkbox') {
            updatedValue = (target as HTMLInputElement).checked;
            if (name === 'requiresEmail' && !updatedValue) {
                updatedValue = false;
                setCurrentStage({
                    ...currentStage,
                    emailTemplateId: null,
                    autoEmail: false,
                    requiresEmail: false
                });
                return;
            }
        }
        else if (name === 'displayOrder' || name === 'daysToAutoAdvance') {
            updatedValue = value === '' ? null : parseInt(value, 10);
        }
        else if (name === 'emailTemplateId' || name === 'advanceToStageId') {
            // Convert empty strings to null for foreign key fields
            updatedValue = value === '' ? null : value;
        }

        setCurrentStage({
            ...currentStage,
            [name]: updatedValue
        });
    };

    const showToast = (message: string, variant: string = 'success') => {
        setToast({
            show: true,
            message,
            variant
        });
    };

    const handleSaveStage = async () => {
        if (!currentStage) return;

        const orderClash = stages.find(s => s.id !== currentStage.id && s.displayOrder === currentStage.displayOrder);
        if (orderClash) {
            showToast(`Display order ${currentStage.displayOrder} is already used by "${orderClash.name}".`, 'danger');
            return;
        }

        try {
            if (currentStage.id) {
                // Update existing stage
                const stageUpdateData: StageUpdateDto = {
                    id: currentStage.id,
                    name: currentStage.name,
                    description: currentStage.description,
                    displayOrder: currentStage.displayOrder,
                    isActive: currentStage.isActive,
                    requiresEmail: currentStage.requiresEmail,
                    emailTemplateId: currentStage.emailTemplateId,
                    autoAdvanceAfterDays: currentStage.autoAdvanceAfterDays,
                    daysToAutoAdvance: currentStage.daysToAutoAdvance,
                    advanceToStageId: currentStage.advanceToStageId,
                    canMoveToFromAnyStage: currentStage.canMoveToFromAnyStage,
                    autoEmail: currentStage.autoEmail
                };

                await dispatch(updateStage({ id: currentStage.id, stageData: stageUpdateData })).unwrap();
                showToast('Stage updated successfully!');
            } else {
                // Create new stage
                const stageCreateData: StageCreateDto = {
                    name: currentStage.name,
                    description: currentStage.description,
                    displayOrder: currentStage.displayOrder,
                    isActive: currentStage.isActive,
                    requiresEmail: currentStage.requiresEmail,
                    emailTemplateId: currentStage.emailTemplateId,
                    autoAdvanceAfterDays: currentStage.autoAdvanceAfterDays,
                    daysToAutoAdvance: currentStage.daysToAutoAdvance,
                    advanceToStageId: currentStage.advanceToStageId,
                    canMoveToFromAnyStage: currentStage.canMoveToFromAnyStage,
                    autoEmail: currentStage.autoEmail
                };

                await dispatch(createStage(stageCreateData)).unwrap();
                showToast('Stage created successfully!');
            }

            handleCloseModal();
        } catch (err) {
            console.error('Error saving stage:', err);
            showToast('Failed to save stage. Please try again.', 'danger');
        }
    };

    const handleDeleteStage = async (stageId: string) => {
        if (!window.confirm('Are you sure you want to delete this stage? This action cannot be undone.')) {
            return;
        }

        try {
            await dispatch(deleteStage(stageId)).unwrap();
            await dispatch(fetchAllStages()).unwrap();
            showToast('Stage deleted successfully!');
        } catch (err) {
            console.error('Error deleting stage:', err);
            showToast('Failed to delete stage. Please try again.', 'danger');
        }
    };

    const getEmailTemplateDisplay = (stage: Stage) => {
        if (!stage.requiresEmail) return 'No';

        const template = emailTemplates.find(t => t.id === stage.emailTemplateId);
        const templateName = template?.name || 'Unknown Template';
        const autoText = stage.autoEmail ? ' (Auto)' : '';

        return `Yes - ${templateName}${autoText}`;
    };

    const getAdvanceToStageDisplay = (stage: Stage) => {
        const advanceTo = stage.advanceToStageId ? stages.find(s => s.id === stage.advanceToStageId) : undefined;
        if (!advanceTo) return <span className="text-muted">-</span>;

        return <span title={advanceTo.name}>{advanceTo.displayOrder}</span>;
    };

    const activeStageCount = stages.filter(stage => stage.isActive).length;
    const inactiveStageCount = stages.filter(stage => !stage.isActive).length;

    return (
        <>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="m-0">Stage Management</h5>
                    <div className="d-flex align-items-center gap-3">
                        {/* Show inactive stages toggle */}
                        <Form.Check
                            type="switch"
                            id="show-inactive-stages"
                            label={`Show inactive stages (${inactiveStageCount})`}
                            checked={showInactiveStages}
                            onChange={(e) => setShowInactiveStages(e.target.checked)}
                            className="mb-0"
                        />
                        <Button
                            variant="primary"
                            onClick={handleCreateStage}
                            disabled={loading}
                        >
                            <i className="bi bi-plus-circle me-1"></i> Add New Stage
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => dispatch(clearError())}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Summary info */}
                    <div className="mb-3">
                        <small className="text-muted">
                            Showing {filteredStages.length} of {stages.length} stages
                            {!showInactiveStages && inactiveStageCount > 0 && (
                                <span> ({activeStageCount} active stages, {inactiveStageCount} inactive stages hidden)</span>
                            )}
                        </small>
                    </div>

                    {loading && stages.length === 0 ? (
                        <LoadingSpinner centered />
                    ) : (
                        <Table responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Order</th>
                                    <th>Advances To</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Email</th>
                                    <th>Candidates</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStages.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center">
                                            {stages.length === 0 ? (
                                                'No stages found. Create a new stage to get started.'
                                            ) : (
                                                'No active stages found. Use the toggle above to show inactive stages.'
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredStages.map(stage => (
                                        <tr
                                            key={stage.id}
                                            className={!stage.isActive ? 'table-secondary' : ''}
                                        >
                                            <td>{stage.displayOrder}</td>
                                            <td>{getAdvanceToStageDisplay(stage)}</td>
                                            <td>
                                                <strong>{stage.name}</strong>
                                                {stage.canMoveToFromAnyStage && (
                                                    <span className="badge bg-info ms-2 small">Any Stage</span>
                                                )}
                                            </td>
                                            <td>
                                                {stage.description ? (
                                                    stage.description.length > 50
                                                        ? `${stage.description.substring(0, 50)}...`
                                                        : stage.description
                                                ) : (
                                                    <span className="text-muted">No description</span>
                                                )}
                                            </td>
                                            <td>{getEmailTemplateDisplay(stage)}</td>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {stage.candidateCount || 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${stage.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                    {stage.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => handleEditStage(stage)}
                                                    disabled={loading}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteStage(stage.id)}
                                                    disabled={loading || (stage.candidateCount && stage.candidateCount > 0)}
                                                    title={stage.candidateCount && stage.candidateCount > 0
                                                        ? "Cannot delete stage with candidates"
                                                        : "Delete stage"}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}

                    {loading && stages.length > 0 && (
                        <div className="text-center mt-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <small className="text-muted">Updating stages...</small>
                        </div>
                    )}

                    {/* Stage Edit/Create Modal */}
                    <Modal show={showModal} onHide={handleCloseModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {currentStage?.id ? 'Edit Stage' : 'Create New Stage'}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {currentStage && (
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Stage Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={currentStage.name}
                                            onChange={handleFormChange}
                                            required
                                            placeholder="Enter stage name"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="description"
                                            value={currentStage.description || ''}
                                            onChange={handleFormChange}
                                            placeholder="Enter stage description"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Display Order *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="displayOrder"
                                            value={currentStage.displayOrder}
                                            onChange={handleFormChange}
                                            required
                                            min={1}
                                        />
                                    </Form.Group>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Is Active"
                                                    name="isActive"
                                                    checked={currentStage.isActive}
                                                    onChange={handleFormChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Can Move To From Any Stage"
                                                    name="canMoveToFromAnyStage"
                                                    checked={currentStage.canMoveToFromAnyStage}
                                                    onChange={handleFormChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Requires Email"
                                                    name="requiresEmail"
                                                    checked={currentStage.requiresEmail}
                                                    onChange={handleFormChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {currentStage.requiresEmail && (
                                        <Row className="mb-3">
                                            <Col md={8}>
                                                <Form.Group>
                                                    <Form.Label>Email Template</Form.Label>
                                                    <Form.Select
                                                        name="emailTemplateId"
                                                        value={currentStage.emailTemplateId || ''}
                                                        onChange={handleFormChange}
                                                        disabled={emailTemplatesLoading}
                                                    >
                                                        <option value="">Select Template</option>
                                                        {emailTemplates.map(template => (
                                                            <option key={template.id} value={template.id}>
                                                                {template.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {emailTemplatesLoading && (
                                                        <small className="text-muted">Loading templates...</small>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>&nbsp;</Form.Label>
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Auto Send Email"
                                                        name="autoEmail"
                                                        checked={currentStage.autoEmail}
                                                        onChange={handleFormChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label>Advance to Stage</Form.Label>
                                                <Form.Select
                                                    name="advanceToStageId"
                                                    value={currentStage.advanceToStageId || ''}
                                                    onChange={handleFormChange}
                                                >
                                                    <option value="">Select Stage (Optional)</option>
                                                    {stages
                                                        .filter(s => s.id !== currentStage.id && s.isActive)
                                                        .map(stage => (
                                                            <option key={stage.id} value={stage.id}>
                                                                {stage.name} {!stage.isActive && '(Inactive)'}
                                                            </option>
                                                        ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSaveStage}
                                disabled={loading || !currentStage?.name.trim()}
                            >
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Stage'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>

            {/* Toast Container */}
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1050 }}>
                <Toast
                    onClose={() => setToast(prev => ({ ...prev, show: false }))}
                    show={toast.show}
                    delay={3000}
                    autohide
                    bg={toast.variant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toast.variant === 'success' ? 'Success' : 'Error'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toast.variant === 'success' ? 'text-white' : ''}>
                        {toast.message}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default StageManagement;